import { useState } from "react";
import { useAdminAuth } from "./AdminAuthContext";
import { useAdminData } from "./AdminDataContext";
import {
  CreditCard,
  Shield,
  ArrowRight,
  CheckCircle2,
  Circle,
  AlertTriangle,
  Webhook,
  Database,
  ExternalLink,
  Mail,
  ChevronDown,
  ChevronUp,
  Zap,
  Globe,
  Lock,
  FileText,
} from "lucide-react";

export function AdminPayments() {
  const { hasPermission } = useAdminAuth();
  const { orders } = useAdminData();
  const [expandedSection, setExpandedSection] = useState<string | null>("overview");

  if (!hasPermission("site_config")) {
    return (
      <p className="text-[#6F675F]" style={{ fontSize: "14px" }}>
        You don't have access to this section.
      </p>
    );
  }

  const totalOrders = orders.length;
  const pendingOrders = orders.filter((o) => o.status === "pending").length;

  const toggleSection = (id: string) => {
    setExpandedSection(expandedSection === id ? null : id);
  };

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <h1
          className="text-[#2D2A26] tracking-[0.08em] mb-2"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "28px", fontWeight: 400 }}
        >
          Payments & Stripe
        </h1>
        <p className="text-[#6F675F]" style={{ fontSize: "14px", fontWeight: 300 }}>
          Payment infrastructure status and Stripe integration information
        </p>
      </div>

      {/* Status Banner */}
      <div className="bg-[#C7A76C]/8 border border-[#C7A76C]/25 rounded-xl p-5 mb-8">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#C7A76C]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Mail size={16} className="text-[#C7A76C]" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-[#2D2A26] mb-1" style={{ fontSize: "14px", fontWeight: 500 }}>
              Current mode: Order Request by Email
            </p>
            <p className="text-[#6F675F]" style={{ fontSize: "13px", fontWeight: 300, lineHeight: 1.6 }}>
              Your shop currently operates as a soft-checkout order request platform. When a customer submits an
              order, no online payment is taken — you receive the request and contact the customer to confirm
              availability, shipping details, and arrange payment manually. This is ideal for artisan and
              small-batch products.
            </p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-5 shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
          <p className="text-[#6F675F] tracking-[0.1em] uppercase mb-1" style={{ fontSize: "10px" }}>
            Total Requests
          </p>
          <p
            className="text-[#2D2A26]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "28px", fontWeight: 400 }}
          >
            {totalOrders}
          </p>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
          <p className="text-[#6F675F] tracking-[0.1em] uppercase mb-1" style={{ fontSize: "10px" }}>
            Awaiting Response
          </p>
          <p
            className="text-[#C7A76C]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "28px", fontWeight: 400 }}
          >
            {pendingOrders}
          </p>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
          <p className="text-[#6F675F] tracking-[0.1em] uppercase mb-1" style={{ fontSize: "10px" }}>
            Payment Provider
          </p>
          <p className="text-[#6F675F] flex items-center gap-2 mt-2" style={{ fontSize: "13px" }}>
            <Circle size={8} className="text-[#C7A76C] fill-[#C7A76C]" />
            Not configured
          </p>
        </div>
      </div>

      {/* What is Stripe Section */}
      <div className="bg-white rounded-xl shadow-[0_2px_16px_rgba(40,32,20,0.04)] mb-6 overflow-hidden">
        <button
          onClick={() => toggleSection("what-is-stripe")}
          className="w-full flex items-center justify-between p-5 text-left hover:bg-[#F7F5F1]/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#635BFF]/10 flex items-center justify-center">
              <CreditCard size={16} className="text-[#635BFF]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[#2D2A26]" style={{ fontSize: "14px", fontWeight: 500 }}>
                What is Stripe?
              </p>
              <p className="text-[#6F675F]" style={{ fontSize: "12px", fontWeight: 300 }}>
                Learn about online payment processing
              </p>
            </div>
          </div>
          {expandedSection === "what-is-stripe" ? (
            <ChevronUp size={18} className="text-[#6F675F]" />
          ) : (
            <ChevronDown size={18} className="text-[#6F675F]" />
          )}
        </button>
        {expandedSection === "what-is-stripe" && (
          <div className="px-5 pb-5 border-t border-[#E6E0D7]">
            <div className="pt-4 space-y-4" style={{ fontSize: "13px", fontWeight: 300, lineHeight: 1.7, color: "#4A453E" }}>
              <p>
                <strong style={{ fontWeight: 500, color: "#2D2A26" }}>Stripe</strong> is the world's leading online
                payment processing platform, trusted by millions of businesses from small artisan shops to companies
                like Shopify, Amazon, and Google. It allows your webshop to accept credit card, debit card, and
                local payment methods (like iDEAL, Bancontact, and SEPA) directly on your website.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-4">
                {[
                  { icon: Shield, label: "PCI-compliant security", desc: "Card data never touches your server" },
                  { icon: Globe, label: "135+ currencies", desc: "Accept payments from anywhere" },
                  { icon: Lock, label: "3D Secure / SCA", desc: "EU Strong Customer Authentication built-in" },
                  { icon: Zap, label: "Instant setup", desc: "Test mode available immediately" },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-2.5 p-3 bg-[#F7F5F1] rounded-lg">
                    <item.icon size={14} className="text-[#635BFF] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <div>
                      <p style={{ fontSize: "12px", fontWeight: 500, color: "#2D2A26" }}>{item.label}</p>
                      <p style={{ fontSize: "11px", color: "#6F675F" }}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p>
                With Stripe, your customers can complete their purchase directly on your website with a secure,
                branded checkout experience. Stripe handles all the complexity of payment processing, fraud
                prevention, and regulatory compliance (PSD2, SCA, GDPR).
              </p>
              <p>
                Stripe charges a per-transaction fee (typically 1.5% + €0.25 for European cards) with no
                monthly fees or setup costs. You can start in test mode for free.
              </p>
              <a
                href="https://stripe.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[#635BFF] hover:underline"
                style={{ fontSize: "12px" }}
              >
                Visit stripe.com
                <ExternalLink size={12} />
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Database Readiness Section */}
      <div className="bg-white rounded-xl shadow-[0_2px_16px_rgba(40,32,20,0.04)] mb-6 overflow-hidden">
        <button
          onClick={() => toggleSection("readiness")}
          className="w-full flex items-center justify-between p-5 text-left hover:bg-[#F7F5F1]/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#6F7F63]/10 flex items-center justify-center">
              <Database size={16} className="text-[#6F7F63]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[#2D2A26]" style={{ fontSize: "14px", fontWeight: 500 }}>
                Database Readiness
              </p>
              <p className="text-[#6F675F]" style={{ fontSize: "12px", fontWeight: 300 }}>
                Stripe-ready tables are already prepared
              </p>
            </div>
          </div>
          {expandedSection === "readiness" ? (
            <ChevronUp size={18} className="text-[#6F675F]" />
          ) : (
            <ChevronDown size={18} className="text-[#6F675F]" />
          )}
        </button>
        {expandedSection === "readiness" && (
          <div className="px-5 pb-5 border-t border-[#E6E0D7]">
            <div className="pt-4">
              <p className="text-[#6F675F] mb-4" style={{ fontSize: "13px", fontWeight: 300, lineHeight: 1.6 }}>
                The database schema has been designed from day one to support Stripe. The following tables
                are already created and ready — no schema migration will be needed when Stripe is activated:
              </p>
              <div className="space-y-2">
                {[
                  { table: "matcha.payments", status: "ready", desc: "Supports email_request, checkout_session, and payment_intent modes" },
                  { table: "matcha.payment_events", status: "ready", desc: "Normalized payment state transition tracking" },
                  { table: "matcha.webhook_events", status: "ready", desc: "Raw Stripe webhook ingestion with deduplication" },
                  { table: "matcha.order_status_history", status: "ready", desc: "Full audit trail for order state changes" },
                  { table: "matcha.customers", status: "ready", desc: "Customer records with stripe_customer_id column" },
                  { table: "matcha.product_prices", status: "ready", desc: "Versioned prices in cents with stripe_price_id mapping" },
                  { table: "matcha.addresses", status: "ready", desc: "Deferred address capture after order confirmation" },
                  { table: "matcha.orders (extended)", status: "ready", desc: "Added order_type, payment_status, fulfillment_status columns" },
                  { table: "matcha.products (extended)", status: "ready", desc: "Added stripe_product_id column" },
                ].map((item) => (
                  <div
                    key={item.table}
                    className="flex items-start gap-3 p-3 bg-[#F7F5F1] rounded-lg"
                  >
                    <CheckCircle2
                      size={14}
                      className="text-[#6F7F63] mt-0.5 flex-shrink-0"
                      strokeWidth={1.5}
                    />
                    <div>
                      <code className="text-[#2D2A26] bg-[#E6E0D7]/50 px-1.5 py-0.5 rounded" style={{ fontSize: "11px" }}>
                        {item.table}
                      </code>
                      <p className="text-[#6F675F] mt-0.5" style={{ fontSize: "11px", lineHeight: 1.5 }}>
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Integration Steps Section */}
      <div className="bg-white rounded-xl shadow-[0_2px_16px_rgba(40,32,20,0.04)] mb-6 overflow-hidden">
        <button
          onClick={() => toggleSection("steps")}
          className="w-full flex items-center justify-between p-5 text-left hover:bg-[#F7F5F1]/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#C7A76C]/10 flex items-center justify-center">
              <ArrowRight size={16} className="text-[#C7A76C]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[#2D2A26]" style={{ fontSize: "14px", fontWeight: 500 }}>
                What Needs to Happen
              </p>
              <p className="text-[#6F675F]" style={{ fontSize: "12px", fontWeight: 300 }}>
                Steps required to activate Stripe payments
              </p>
            </div>
          </div>
          {expandedSection === "steps" ? (
            <ChevronUp size={18} className="text-[#6F675F]" />
          ) : (
            <ChevronDown size={18} className="text-[#6F675F]" />
          )}
        </button>
        {expandedSection === "steps" && (
          <div className="px-5 pb-5 border-t border-[#E6E0D7]">
            <div className="pt-4 space-y-4">
              {[
                {
                  step: 1,
                  title: "Create a Stripe account",
                  desc: "Sign up at stripe.com. You can start in test mode immediately — no business verification needed for testing.",
                  status: "todo" as const,
                },
                {
                  step: 2,
                  title: "Store API keys as secrets",
                  desc: "Add your Stripe Secret Key, Publishable Key, and Webhook Secret to Supabase secrets. Never expose secret keys in frontend code.",
                  status: "todo" as const,
                },
                {
                  step: 3,
                  title: "Sync products to Stripe",
                  desc: "Create corresponding Product and Price objects in Stripe for each MOECHA product. Store the returned IDs in the database.",
                  status: "todo" as const,
                },
                {
                  step: 4,
                  title: "Create checkout session endpoint",
                  desc: "Build a Supabase Edge Function that creates a Stripe Checkout Session, stores it in the payments table, and returns the redirect URL.",
                  status: "todo" as const,
                },
                {
                  step: 5,
                  title: "Build webhook handler",
                  desc: "Create a secure endpoint that receives Stripe webhook events, verifies signatures, and updates order/payment status automatically.",
                  status: "todo" as const,
                },
                {
                  step: 6,
                  title: "Update checkout flow",
                  desc: "Modify the checkout page to redirect to Stripe's hosted checkout after order creation. Update the confirmation page to verify payment.",
                  status: "todo" as const,
                },
                {
                  step: 7,
                  title: "Test with test cards",
                  desc: "Use Stripe's test card numbers (4242 4242 4242 4242) to verify the full flow before going live.",
                  status: "todo" as const,
                },
                {
                  step: 8,
                  title: "Go live",
                  desc: "Switch from test keys to live keys, enable real payment methods, and configure your payout bank account in Stripe Dashboard.",
                  status: "todo" as const,
                },
              ].map((item) => (
                <div key={item.step} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 border"
                      style={{
                        borderColor: "#E6E0D7",
                        background: "#F7F5F1",
                      }}
                    >
                      <span className="text-[#6F675F]" style={{ fontSize: "11px" }}>
                        {item.step}
                      </span>
                    </div>
                    {item.step < 8 && <div className="w-px h-full bg-[#E6E0D7] mt-1" />}
                  </div>
                  <div className="pb-4">
                    <p className="text-[#2D2A26] mb-0.5" style={{ fontSize: "13px", fontWeight: 500 }}>
                      {item.title}
                    </p>
                    <p className="text-[#6F675F]" style={{ fontSize: "12px", fontWeight: 300, lineHeight: 1.6 }}>
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Webhook Info Section */}
      <div className="bg-white rounded-xl shadow-[0_2px_16px_rgba(40,32,20,0.04)] mb-6 overflow-hidden">
        <button
          onClick={() => toggleSection("webhooks")}
          className="w-full flex items-center justify-between p-5 text-left hover:bg-[#F7F5F1]/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#8A6D63]/10 flex items-center justify-center">
              <Webhook size={16} className="text-[#8A6D63]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[#2D2A26]" style={{ fontSize: "14px", fontWeight: 500 }}>
                Webhook Events
              </p>
              <p className="text-[#6F675F]" style={{ fontSize: "12px", fontWeight: 300 }}>
                How Stripe communicates payment results
              </p>
            </div>
          </div>
          {expandedSection === "webhooks" ? (
            <ChevronUp size={18} className="text-[#6F675F]" />
          ) : (
            <ChevronDown size={18} className="text-[#6F675F]" />
          )}
        </button>
        {expandedSection === "webhooks" && (
          <div className="px-5 pb-5 border-t border-[#E6E0D7]">
            <div className="pt-4 space-y-3" style={{ fontSize: "13px", fontWeight: 300, lineHeight: 1.6, color: "#4A453E" }}>
              <p>
                Stripe uses <strong style={{ fontWeight: 500 }}>webhooks</strong> to notify your server about
                payment events in real time. When a customer completes (or fails) a payment, Stripe sends an HTTP
                POST to your endpoint with the event details. This is the recommended way to confirm payments —
                never rely solely on client-side redirects.
              </p>
              <p>The following events are pre-configured to be handled:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 my-3">
                {[
                  "checkout.session.completed",
                  "checkout.session.expired",
                  "checkout.session.async_payment_succeeded",
                  "checkout.session.async_payment_failed",
                  "payment_intent.succeeded",
                  "payment_intent.payment_failed",
                  "charge.refunded",
                ].map((evt) => (
                  <div key={evt} className="flex items-center gap-2 p-2 bg-[#F7F5F1] rounded">
                    <Circle size={5} className="text-[#6F675F] fill-[#6F675F] flex-shrink-0" />
                    <code style={{ fontSize: "11px", color: "#2D2A26" }}>{evt}</code>
                  </div>
                ))}
              </div>
              <p>
                Raw webhook payloads are stored in the <code className="bg-[#E6E0D7]/50 px-1 py-0.5 rounded" style={{ fontSize: "11px" }}>webhook_events</code> table
                for replay safety and deduplication. Each event is verified using Stripe's signing secret before processing.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Handover Note Section */}
      <div className="bg-white rounded-xl shadow-[0_2px_16px_rgba(40,32,20,0.04)] mb-6 overflow-hidden">
        <button
          onClick={() => toggleSection("handover")}
          className="w-full flex items-center justify-between p-5 text-left hover:bg-[#F7F5F1]/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#A7B39A]/15 flex items-center justify-center">
              <FileText size={16} className="text-[#6F7F63]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[#2D2A26]" style={{ fontSize: "14px", fontWeight: 500 }}>
                Technical Handover
              </p>
              <p className="text-[#6F675F]" style={{ fontSize: "12px", fontWeight: 300 }}>
                Documentation for developers
              </p>
            </div>
          </div>
          {expandedSection === "handover" ? (
            <ChevronUp size={18} className="text-[#6F675F]" />
          ) : (
            <ChevronDown size={18} className="text-[#6F675F]" />
          )}
        </button>
        {expandedSection === "handover" && (
          <div className="px-5 pb-5 border-t border-[#E6E0D7]">
            <div className="pt-4 space-y-3" style={{ fontSize: "13px", fontWeight: 300, lineHeight: 1.6, color: "#4A453E" }}>
              <p>
                A detailed handover document has been prepared at{" "}
                <code className="bg-[#E6E0D7]/50 px-1.5 py-0.5 rounded" style={{ fontSize: "11px" }}>
                  /src/imports/stripe-handover.md
                </code>{" "}
                containing:
              </p>
              <ul className="space-y-1.5 ml-4">
                {[
                  "Complete step-by-step integration guide with code examples",
                  "Database schema reference (all Stripe tables and their purpose)",
                  "Stripe Checkout Session creation flow",
                  "Webhook handler implementation guide",
                  "Frontend modification checklist",
                  "Test card numbers and testing procedures",
                  "Security best practices (signature verification, idempotency)",
                  "EU payment method recommendations (iDEAL, Bancontact, SEPA)",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[#6F7F63] mt-1">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p>
                The reference schema from the original planning is at{" "}
                <code className="bg-[#E6E0D7]/50 px-1.5 py-0.5 rounded" style={{ fontSize: "11px" }}>
                  /src/imports/stripe-db-schema.md
                </code>
                .
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Important Notice */}
      <div className="bg-[#8B5C58]/5 border border-[#8B5C58]/15 rounded-xl p-5 mb-6">
        <div className="flex items-start gap-3">
          <AlertTriangle size={16} className="text-[#8B5C58] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
          <div style={{ fontSize: "13px", fontWeight: 300, lineHeight: 1.6 }}>
            <p className="text-[#2D2A26] mb-2" style={{ fontWeight: 500 }}>
              Integration requires development work
            </p>
            <p className="text-[#4A453E]">
              While the database and schema are fully prepared for Stripe, activating live payments requires
              server-side code (Supabase Edge Functions), webhook handling, security configuration, and thorough
              testing. This is not a plug-and-play feature — it needs proper development and testing before
              processing real payments.
            </p>
            <p className="text-[#4A453E] mt-2">
              <strong style={{ fontWeight: 500 }}>Recommended approach:</strong> Get in touch with a developer
              experienced in Stripe and Supabase to handle the integration, testing, and go-live process. The
              database foundation is in place — implementation typically takes 2–4 days of focused development
              time.
            </p>
          </div>
        </div>
      </div>

      {/* Contact CTA */}
      <div className="bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)] text-center">
        <div className="w-12 h-12 rounded-full bg-[#635BFF]/10 flex items-center justify-center mx-auto mb-4">
          <CreditCard size={20} className="text-[#635BFF]" strokeWidth={1.5} />
        </div>
        <p
          className="text-[#2D2A26] mb-2"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 400 }}
        >
          Ready to accept payments?
        </p>
        <p className="text-[#6F675F] mb-4 max-w-md mx-auto" style={{ fontSize: "13px", fontWeight: 300, lineHeight: 1.6 }}>
          Contact a developer to complete the Stripe integration. The database schema is ready —
          you just need the server-side logic and testing to go live.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href="https://stripe.com/register"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#635BFF] text-white rounded-full hover:bg-[#4B45DB] transition-colors"
            style={{ fontSize: "12px", letterSpacing: "0.1em" }}
          >
            Create Stripe Account
            <ExternalLink size={12} />
          </a>
          <a
            href="https://stripe.com/docs"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#E6E0D7] text-[#6F675F] rounded-full hover:border-[#635BFF] hover:text-[#635BFF] transition-colors"
            style={{ fontSize: "12px", letterSpacing: "0.1em" }}
          >
            Stripe Documentation
            <ExternalLink size={12} />
          </a>
        </div>
      </div>
    </div>
  );
}